// V5.31 HARMONISATION : Sidebar Contextuelle avec Toggle
// MODE DASHBOARD : Menu simplifié (Accueil, Mes Missions, Paiement, Parrainage, Zero Trace, Mon Compte)
// MODE MISSION : Étapes de la mission + Retour au Dashboard
// V5.31 : Après validation, basculer en mode Navigation Standard
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, CreditCard, FileText, PenTool, Scan, Shield, Gift, Bot, Lock,
  User, LogOut, ChevronRight, Bell, Sparkles, Menu, X, CheckCircle,
  ArrowLeft, Package, Briefcase, MessageSquare
} from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { API } from '../../App';

// V5.31 : Configuration des étapes par type de mission - Tunnel simplifié (sans étape 4 intermédiaire)
const MISSION_STEPS = {
  demenagement: [
    { id: 'adresses', label: 'Adresses', icon: FileText },
    { id: 'strategie', label: 'Stratégie', icon: Sparkles },  // V5.16 : Liquidation vs Transfert
    { id: 'technique', label: 'Technique', icon: FileText },  // V5.16 : Collecte conditionnelle
    { id: 'synthese', label: 'Synthèse', icon: CheckCircle }  // V5.31 : Renommé + fusion avec signature
  ],
  succession: [
    { id: 'defunt', label: 'Informations', icon: FileText },
    { id: 'organismes', label: 'Organismes', icon: Briefcase },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'mandat', label: 'Signature', icon: PenTool }
  ],
  audit: [
    { id: 'upload', label: 'Upload Relevé', icon: Package },
    { id: 'analyse', label: 'Analyse IA', icon: Scan },
    { id: 'plan', label: 'Plan d\'Action', icon: CheckCircle }
  ]
};

// ═══════════════════════════════════════════════════════════════════════════
// MODE DASHBOARD : Menu simplifié - V5.4 Stabilisation
// ═══════════════════════════════════════════════════════════════════════════
const DASHBOARD_MENU_ITEMS = [
  { id: 'overview', label: 'Accueil', icon: Home, description: 'Vue d\'ensemble de votre dossier' },
  { id: 'missions', label: 'Mes Missions', icon: Briefcase, description: 'Vos missions actives', highlight: true, route: '/dashboard/missions' },
  { id: 'payment', label: 'Paiement', icon: CreditCard, description: 'Gérer votre abonnement' },
  { id: 'referral', label: 'Parrainage', icon: Gift, description: 'Invitez vos proches', special: true },
  { id: 'data-security', label: 'Zero Trace', icon: Lock, description: 'Données & Sécurité' },
  { id: 'support', label: 'Support', icon: MessageSquare, description: 'Mes tickets · Réponse 24h' },
];

// ═══════════════════════════════════════════════════════════════════════════
// Composant : Progress Steps (MODE MISSION - Vertical)
// V5.31 : Masque les étapes après validation et affiche le menu standard
// ═══════════════════════════════════════════════════════════════════════════
export const MissionProgressSidebar = ({ missionType, currentStep, completedSteps = [], onStepClick, isMissionValidated = false }) => {
  const steps = MISSION_STEPS[missionType] || [];
  
  // V5.31 : Si la mission est validée, ne pas afficher les étapes
  if (isMissionValidated) {
    return null;
  }
  
  // Titre selon le type de mission
  const missionTitles = {
    demenagement: 'Mission Déménagement',
    succession: 'Mission Héritage',
    audit: 'Audit Pouvoir d\'Achat'
  };
  
  return (
    <div className="p-4">
      {/* Titre de la mission */}
      <h3 className="text-cyan-400 text-sm font-orbitron font-bold mb-2">
        {missionTitles[missionType] || 'Ma Mission'}
      </h3>
      <p className="text-gray-500 text-xs font-mono mb-4 uppercase tracking-wider">
        // ÉTAPES_EN_COURS
      </p>
      
      <div className="relative">
        {/* Ligne de progression verticale */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-700" />
        
        <div className="space-y-1">
          {steps.map((step, idx) => {
            const StepIcon = step.icon;
            const isCompleted = completedSteps.includes(step.id) || idx < currentStep - 1;
            const isCurrent = idx === currentStep - 1;
            const isClickable = isCompleted || isCurrent;
            
            return (
              <motion.button
                key={step.id}
                onClick={() => isClickable && onStepClick?.(idx + 1)}
                disabled={!isClickable}
                className={`relative w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                  isCurrent 
                    ? 'bg-cyan-400/10 border border-cyan-400/50' 
                    : isCompleted 
                      ? 'hover:bg-gray-800/50 cursor-pointer' 
                      : 'opacity-50 cursor-not-allowed'
                }`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
                data-testid={`step-${step.id}`}
              >
                {/* Indicateur de statut */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                  isCompleted 
                    ? 'bg-success text-black' 
                    : isCurrent 
                      ? 'bg-cyan-400/20 border-2 border-cyan-400 text-cyan-400' 
                      : 'bg-gray-800 border border-gray-700 text-gray-500'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <span className="text-sm font-bold">{idx + 1}</span>
                  )}
                </div>
                
                {/* Label */}
                <div className="flex-1 text-left">
                  <span className={`text-sm font-mono ${
                    isCurrent ? 'text-cyan-400 font-bold' : isCompleted ? 'text-white' : 'text-gray-500'
                  }`}>
                    {step.label}
                  </span>
                </div>
                
                {/* Icône */}
                <StepIcon className={`w-4 h-4 ${
                  isCurrent ? 'text-cyan-400' : isCompleted ? 'text-success' : 'text-gray-600'
                }`} />
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Composant : Stepper Mobile (Horizontal)
// ═══════════════════════════════════════════════════════════════════════════
export const MobileMissionStepper = ({ missionType, currentStep, completedSteps = [] }) => {
  const steps = MISSION_STEPS[missionType] || [];
  
  return (
    <div className="sticky top-0 z-40 bg-void/95 backdrop-blur-md border-b border-gray-800 px-4 py-3">
      <div className="flex items-center justify-between max-w-lg mx-auto">
        {steps.map((step, idx) => {
          const isCompleted = completedSteps.includes(step.id) || idx < currentStep - 1;
          const isCurrent = idx === currentStep - 1;
          
          return (
            <div key={step.id} className="flex items-center">
              {/* Indicateur */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                isCompleted 
                  ? 'bg-success text-black' 
                  : isCurrent 
                    ? 'bg-cyan-400 text-black' 
                    : 'bg-gray-800 text-gray-500'
              }`}>
                {isCompleted ? <CheckCircle className="w-4 h-4" /> : idx + 1}
              </div>
              
              {/* Ligne de connexion */}
              {idx < steps.length - 1 && (
                <div className={`w-8 sm:w-12 h-0.5 mx-1 ${
                  isCompleted ? 'bg-success' : 'bg-gray-700'
                }`} />
              )}
            </div>
          );
        })}
      </div>
      
      {/* Label de l'étape courante */}
      <p className="text-center text-xs text-cyan-400 font-mono mt-2">
        {steps[currentStep - 1]?.label || 'Étape'}
      </p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Composant Principal : Sidebar Contextuelle (Desktop)
// ═══════════════════════════════════════════════════════════════════════════
export const ContextualSidebar = ({ 
  context = 'dashboard', // 'dashboard' | 'mission'
  missionType = null,    // 'demenagement' | 'succession' | 'audit'
  missionId = null,      // ID de la mission en cours
  currentStep = 1,
  completedSteps = [],
  onStepClick,
  activeTab, 
  setActiveTab, 
  user, 
  onLogout, 
  activeSubscription, 
  pushNotifications 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // V5.34 FIX: Détecter le mode (Dashboard vs Mission)
  // /missions/manage est une page de GESTION POST-VALIDATION, pas un tunnel de mission
  const isMissionPath = location.pathname.includes('/mission') && !location.pathname.includes('/missions/manage');
  const isMissionContext = context === 'mission' || isMissionPath;
  
  // Handler pour charger une mission spécifique
  const handleMissionClick = (missionType, missionId) => {
    // Naviguer vers le tunnel de la mission avec l'ID
    const routes = {
      demenagement: '/missions/demenagement',
      succession: '/missions/succession',
      audit: '/dashboard?tab=audit'
    };
    const route = routes[missionType] || '/dashboard';
    navigate(missionId ? `${route}?mission=${missionId}` : route);
  };
  
  return (
    <aside
      // V1.2 FIX sidebar : sticky top-0 + self-start pour rester visible et pleine
      // hauteur même quand le contenu principal du tunnel est plus long que le
      // viewport (évite la zone noire vide sous la sidebar en scrollant).
      className="hidden md:flex w-64 bg-accent/50 border-r border-gray-800 flex-col h-screen sticky top-0 self-start"
      data-testid="contextual-sidebar"
    >
      {/* Header - FIXÉ en haut */}
      <div className="p-6 border-b border-gray-800 flex-shrink-0">
        <Link to="/dashboard" className="block hover:opacity-80 transition-opacity">
          <h1 
            className="font-orbitron text-2xl font-bold text-cyan-400 text-center" 
            style={{ textShadow: '0 0 20px rgba(0, 245, 255, 0.5)' }}
          >
            L'EFFACEUR
          </h1>
          <p className="text-gray-500 text-xs text-center font-mono mt-1">
            {isMissionContext ? '// MISSION_EN_COURS' : '// TABLEAU_DE_BORD'}
          </p>
        </Link>
      </div>
      
      {/* Navigation principale - FLEXIBLE avec scroll si nécessaire */}
      <nav className="flex-1 p-4 overflow-y-auto min-h-0">
        
        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* MODE MISSION : Bouton Retour + Étapes */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {isMissionContext && (
          <>
            <Link 
              to="/dashboard" 
              className="w-full flex items-center gap-3 px-4 py-3 mb-4 text-gray-400 hover:bg-gray-800 transition-all font-mono text-sm border border-gray-700/50 hover:border-cyan-400/50 rounded-lg"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour au Dashboard</span>
            </Link>
            
            {missionType && (
              <MissionProgressSidebar
                missionType={missionType}
                currentStep={currentStep}
                completedSteps={completedSteps}
                onStepClick={onStepClick}
              />
            )}
          </>
        )}
        
        {/* ═══════════════════════════════════════════════════════════════ */}
        {/* MODE DASHBOARD : Menu simplifié - V5.4 STABILISÉ */}
        {/* ═══════════════════════════════════════════════════════════════ */}
        {!isMissionContext && (
          <>
            {/* Menu Dashboard - Pas de lien externe qui casserait la session */}
            <div className="space-y-1">
              {DASHBOARD_MENU_ITEMS.map((item) => {
                const isActive = activeTab === item.id;
                
                return (
                  <button 
                    key={item.id} 
                    onClick={() => setActiveTab(item.id)} 
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all font-mono text-sm ${
                      isActive 
                        ? 'bg-cyan-400/10 border-l-4 border-cyan-400 text-cyan-400' 
                        : item.special 
                          ? 'text-magenta-400 hover:bg-magenta-400/10 border border-magenta-400/30' 
                          : item.highlight 
                            ? 'text-success hover:bg-success/10 border border-success/30' 
                            : 'text-gray-400 hover:bg-gray-800'
                    }`} 
                    data-testid={`nav-${item.id}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <div className="flex-1 text-left">
                      <span className="block">{item.label}</span>
                      {item.description && (
                        <span className="text-xs text-gray-500 block">{item.description}</span>
                      )}
                    </div>
                    {item.special && <Sparkles className="w-4 h-4 animate-pulse" />}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </nav>
      
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* FOOTER FIXE - Alertes + Profil + Déconnexion */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 border-t border-gray-800">
        {/* CCC.13 / DDD.4 — Toggle notifications NAVIGATEUR (différent du banner email !)
            - Renommé "Notifications navigateur" pour lever la confusion avec le banner "Vérifie ton email"
            - Couleur cyan (info, pas warning) — le warning orange est réservé au banner email non-vérifié
            - CACHÉ si non supporté OU si email pas encore vérifié (priorité au banner email) */}
        {pushNotifications?.isSupported === true && !isMissionContext && user?.email_verified !== false && (
          <div className="px-3 py-3 border-b border-gray-800/50">
            <button
              onClick={pushNotifications.toggle}
              disabled={pushNotifications.isLoading}
              className={`w-full flex items-start gap-3 p-3 rounded-lg border transition-all ${
                pushNotifications.isSubscribed
                  ? 'bg-success/10 border-success/30 hover:bg-success/15'
                  : 'bg-cyan-400/5 border-cyan-400/30 hover:bg-cyan-400/10'
              }`}
              data-testid="alerts-toggle"
            >
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                pushNotifications.isSubscribed
                  ? 'bg-success/20 border border-success/40'
                  : 'bg-cyan-400/15 border border-cyan-400/40'
              }`}>
                <Bell className={`w-4 h-4 ${pushNotifications.isSubscribed ? 'text-success' : 'text-cyan-400'}`} />
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className={`text-xs font-bold uppercase tracking-wider mb-0.5 ${
                  pushNotifications.isSubscribed ? 'text-success' : 'text-cyan-300'
                }`}>
                  {pushNotifications.isLoading
                    ? 'Chargement...'
                    : pushNotifications.isSubscribed
                      ? 'Notifications navigateur activées'
                      : 'Notifications navigateur'}
                </p>
                <p className="text-[11px] text-gray-400 leading-snug">
                  {pushNotifications.isSubscribed
                    ? 'Alerte sur cet appareil à chaque étape de vos missions.'
                    : 'Recevoir un ping sur cet appareil quand un organisme répond. Optionnel — les emails passent toujours.'}
                </p>
              </div>
            </button>
          </div>
        )}
        
        {/* Profil & Déconnexion - TOUJOURS visible */}
        <div className="p-4">
          <Link
            to="/profile"
            className="flex items-center gap-3 mb-3 p-3 bg-gradient-to-r from-cyan-500/20 to-magenta-500/20 border border-cyan-400/50 hover:border-cyan-400 hover:from-cyan-500/30 hover:to-magenta-500/30 transition-all group rounded-lg"
            data-testid="profile-link"
          >
            <div className="w-10 h-10 bg-cyan-400/30 group-hover:bg-cyan-400/40 flex items-center justify-center rounded-lg transition-colors">
              <User className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-cyan-400 text-sm font-bold font-orbitron">MON COMPTE</p>
              <p className="text-gray-400 text-xs truncate">{user?.email}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 transition-transform" />
          </Link>
          {/* V1.2 — Bouton Administration visible uniquement pour l'admin */}
          {(user?.is_admin === true || (user?.email || '').toLowerCase() === 'leffaceur@effaceurpro.fr') && (
            <Link
              to="/admin"
              className="flex items-center gap-3 mb-3 p-3 bg-gradient-to-r from-magenta-500/10 to-orange-500/10 border border-magenta-400/40 hover:border-magenta-400 hover:from-magenta-500/20 hover:to-orange-500/20 transition-all group rounded-lg"
              data-testid="admin-link"
            >
              <div className="w-10 h-10 bg-magenta-400/20 group-hover:bg-magenta-400/30 flex items-center justify-center rounded-lg transition-colors">
                <Shield className="w-5 h-5 text-magenta-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-magenta-400 text-sm font-bold font-orbitron">ADMINISTRATION</p>
                <p className="text-gray-400 text-xs truncate">Gestion globale · Pros · Codes</p>
              </div>
              <ChevronRight className="w-5 h-5 text-magenta-400 group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
          <Button
            variant="ghost"
            onClick={onLogout}
            className="w-full justify-start text-red-400 hover:bg-red-400/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </div>
    </aside>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// Menu Mobile (Burger)
// ═══════════════════════════════════════════════════════════════════════════
export const MobileMenu = ({ 
  activeTab, 
  setActiveTab, 
  user, 
  onLogout, 
  isOpen, 
  setIsOpen,
  context = 'dashboard',
  missionType = null,
  currentStep = 1,
  completedSteps = []
}) => {
  // Menu simplifié pour mobile
  const mobileMenuItems = [
    { id: 'overview', label: 'Accueil', icon: Home },
    { id: 'payment', label: 'Paiement', icon: CreditCard },
    { id: 'referral', label: 'Parrainage', icon: Gift },
    { id: 'data-security', label: 'Zero Trace', icon: Lock },
    { id: 'support', label: 'Support', icon: MessageSquare },
  ];
  
  return (
    <>
      {/* Bouton Burger */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-gray-800/80 backdrop-blur rounded-lg border border-gray-700"
        data-testid="mobile-menu-toggle"
      >
        {isOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
      </button>
      
      {/* Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: -300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            className="md:hidden fixed inset-0 z-40 bg-void/95 backdrop-blur-lg"
          >
            <div className="flex flex-col h-full pt-16 p-4">
              <h1 className="font-orbitron text-2xl font-bold text-cyan-400 text-center mb-6">
                L'EFFACEUR
              </h1>
              
              {/* Navigation */}
              <nav className="flex-1 space-y-2">
                {context === 'mission' && missionType ? (
                  // MODE MISSION : Afficher les étapes
                  <>
                    <Link 
                      to="/dashboard"
                      onClick={() => setIsOpen(false)}
                      className="w-full flex items-center gap-3 px-4 py-3 mb-4 text-gray-400 hover:bg-gray-800 rounded-lg"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span>Retour au Dashboard</span>
                    </Link>
                    <MissionProgressSidebar
                      missionType={missionType}
                      currentStep={currentStep}
                      completedSteps={completedSteps}
                    />
                  </>
                ) : (
                  // MODE DASHBOARD : Menu simplifié
                  mobileMenuItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setActiveTab(item.id);
                        setIsOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        activeTab === item.id
                          ? 'bg-cyan-400/20 text-cyan-400 border border-cyan-400/50'
                          : 'text-gray-400 hover:bg-gray-800'
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="font-mono">{item.label}</span>
                    </button>
                  ))
                )}
              </nav>
              
              {/* Footer */}
              <div className="border-t border-gray-800 pt-4 space-y-2">
                <Link
                  to="/profile"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg"
                >
                  <User className="w-5 h-5 text-cyan-400" />
                  <span className="text-white">{user?.name || user?.email}</span>
                </Link>
                {/* V1.2 — Administration (visible uniquement pour l'admin) */}
                {(user?.is_admin === true || (user?.email || '').toLowerCase() === 'leffaceur@effaceurpro.fr') && (
                  <Link
                    to="/admin"
                    onClick={() => setIsOpen(false)}
                    className="flex items-center gap-3 p-3 bg-magenta-500/10 border border-magenta-400/40 rounded-lg"
                  >
                    <Shield className="w-5 h-5 text-magenta-400" />
                    <span className="text-magenta-400 font-bold">ADMINISTRATION</span>
                  </Link>
                )}
                <Button
                  variant="ghost"
                  onClick={onLogout}
                  className="w-full justify-start text-red-400"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ContextualSidebar;
